import React, { useState } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0); // First one open by default

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How is this possible?",
      answer: "We can offer higher rates by keeping things simple. Saverr is a no-frills savings account—no ATM withdrawals, no bill pay, no checks, no debit cards. Just easy ACH deposits and withdrawals. By eliminating costly infrastructure like physical branches, ATM networks, and card programs, we pass those savings directly to you in the form of higher interest rates. It's banking stripped down to what matters most: <strong style='font-size: 1.2em;'>growing your money</strong>."
    },
    {
      question: "What can I do with my Saverr account?",
      answer: "You can deposit funds via ACH transfer from your existing bank account and withdraw funds the same way. That's it—and that's exactly how we keep rates high. Think of Saverr as your dedicated high-yield savings vehicle, not your everyday spending account."
    },
    {
      question: "How do I access my money?",
      answer: "Simply initiate an ACH transfer to your linked bank account. Transfers typically take 1-2 business days. While you won't have instant access like an ATM, the trade-off is a significantly higher yield on your savings."
    },
    {
      question: "Is this a real bank account?",
      answer: "Saverr partners with FDIC-insured banks to hold your deposits. Your funds receive the same $250,000 FDIC insurance protection as any traditional bank account, but with better rates and lower overhead."
    }
  ];

  const features = [
    { name: 'ACH Deposits', included: true },
    { name: 'ACH Withdrawals', included: true },
    { name: 'High Interest Rates', included: true },
    { name: 'FDIC Insurance', included: true },
    { name: 'ATM Withdrawals', included: false },
    { name: 'Debit Cards', included: false },
    { name: 'Bill Pay', included: false },
    { name: 'Paper Checks', included: false }
  ];

  return (
    <section id="faq" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Everything you need to know about how Saverr works
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-lg font-bold text-slate-900 pr-4">
                    {faq.question}
                  </span>
                  <div
                    className={`flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </div>
              </div>
            ))}
          </div>

          {/* What's Included Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm h-fit sticky top-24">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">What's Included</h3>
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                >
                  <span className={`font-medium ${
                    feature.included ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {feature.name}
                  </span>
                  {feature.included ? (
                    <div className="bg-green-100 p-1 rounded-full">
                      <Check className="w-5 h-5 text-green-600" strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="bg-slate-100 p-1 rounded-full">
                      <X className="w-5 h-5 text-slate-400" strokeWidth={3} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-sm text-blue-900 font-medium">
                <strong>The trade-off:</strong> By removing expensive features, we deliver rates up to 10x higher than traditional banks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
